import React, { Fragment, useEffect, useState } from 'react'
import { MP } from '@newageerp/crm-ui'
import { DashboardGrossCustomAdSpendItem, DashboardGrossCustomConfig, DashboardGrossCustomFilter, DashboardGrossCustomGrossPerfItem, DashboardGrossCustomGroupBy } from './types';
import { Table, Card } from '@newageerp/crm-ui';
import { numberFormat } from '@newageerp/v3.bundles.utils-bundle';
import classNames from 'classnames';
import { CrmAdsFields, CrmAdsFromFb, CrmAdsFromFbAll, CrmAdsFromGoogleAll, CrmAdsFromTk, CrmAdsFromTkAll, CrmGrossFields, CrmGrossFrom, DashboardGrossCustomAdSpendAggregate, DashboardGrossCustomAdSpendFilter, DashboardGrossCustomAdSpendFilterAll, DashboardGrossCustomDataAggregate, DashboardGrossCustomDataFilter, DashboardGrossCustomDataSort, DashboardGrossCustomRecurringFilter, DashboardGrossCustomRecurringGroupByPath } from './utils';
import { useTemplatesCore } from '@newageerp/v3.templates.templates-core';
import { checkPermission, Permissions } from '../../Permissions/Permissions';

type Props = {
    groupBy: DashboardGrossCustomGroupBy,
    config: DashboardGrossCustomConfig,
    reloadKey: number
    filter: DashboardGrossCustomFilter,
    loading: {
        get: boolean,
        set: (v: boolean) => void,
    }
}

export default function DashboardGrossCustomPivot(props: Props) {
    const [doGrossPerfReq, grosPerfData] = MP.useMpDataRequest(CrmGrossFrom);
    const [doRecurringReq, recurringData] = MP.useMpDataRequest<DashboardGrossCustomAdSpendItem>('payment-recurring-report');

    const [doAdSpendFbReq, adSpendFbData] = MP.useMpDataRequest<DashboardGrossCustomAdSpendItem>(CrmAdsFromFb);
    const [doAdSpendTkReq, adSpendTkData] = MP.useMpDataRequest<DashboardGrossCustomAdSpendItem>(CrmAdsFromTk);

    const [doAdSpendFbAllReq, adSpendFbAllData] = MP.useMpDataRequest<DashboardGrossCustomAdSpendItem>(CrmAdsFromFbAll);
    const [doAdSpendTkAllReq, adSpendTkAllData] = MP.useMpDataRequest<DashboardGrossCustomAdSpendItem>(CrmAdsFromTkAll);
    const [doAdSpendGoogleAllReq, adSpendGoogleAllData] = MP.useMpDataRequest<DashboardGrossCustomAdSpendItem>(CrmAdsFromGoogleAll);

    const [data, setData] = useState<DashboardGrossCustomGrossPerfItem[]>([])

    const { userState } = useTemplatesCore();
    const isAdmin = checkPermission(userState, Permissions.Enum.admin);

    const loadGrossPerformanceData = async () => {

        if (props.config.filters.ad_cost) {
            await doAdSpendFbReq({
                limit: -1,
                offset: 0,
                aggregate: DashboardGrossCustomAdSpendAggregate,
                columns: [props.groupBy],
                dataFilter: DashboardGrossCustomAdSpendFilter(props.config, props.filter),
                fields: CrmAdsFields,
            })
            await doAdSpendTkReq({
                limit: -1,
                offset: 0,
                aggregate: DashboardGrossCustomAdSpendAggregate,
                columns: [props.groupBy],
                dataFilter: DashboardGrossCustomAdSpendFilter(props.config, props.filter),
                fields: CrmAdsFields,
            })
            if (props.config.filters.ad_cost_substraction) {
                await doAdSpendFbAllReq({
                    limit: -1,
                    offset: 0,
                    aggregate: DashboardGrossCustomAdSpendAggregate,
                    columns: [props.groupBy],
                    dataFilter: DashboardGrossCustomAdSpendFilterAll(props.config, props.filter),
                    fields: CrmAdsFields,
                })
                await doAdSpendTkAllReq({
                    limit: -1,
                    offset: 0,
                    aggregate: DashboardGrossCustomAdSpendAggregate,
                    columns: [props.groupBy],
                    dataFilter: DashboardGrossCustomAdSpendFilterAll(props.config, props.filter),
                    fields: CrmAdsFields,
                })
                await doAdSpendGoogleAllReq({
                    limit: -1,
                    offset: 0,
                    aggregate: DashboardGrossCustomAdSpendAggregate,
                    columns: [props.groupBy],
                    dataFilter: DashboardGrossCustomAdSpendFilterAll(props.config, props.filter),
                    fields: CrmAdsFields,
                })
            }
        }
        if (props.config.filters.recurring) {
            const groupByColumn = DashboardGrossCustomRecurringGroupByPath(props.groupBy);
            await doRecurringReq({
                limit: -1,
                offset: 0,
                aggregate: [
                    {
                        title: "",
                        function: 'countDistinct',
                        column: 'order_id'
                    },
                    {
                        title: "",
                        function: 'sum',
                        column: 'total'
                    }
                ],
                columns: [
                    groupByColumn
                ],
                dataFilter: DashboardGrossCustomRecurringFilter(props.config, props.filter),
                sort: [
                    {
                        "column": groupByColumn,
                        "direction": "ASC"
                    }
                ]
            })
        }

        if (props.config.filters.gross) {
            const res = await doGrossPerfReq({
                limit: -1,
                offset: 0,
                aggregate: DashboardGrossCustomDataAggregate,
                columns: [props.groupBy],
                dataFilter: DashboardGrossCustomDataFilter(props.config, props.filter),
                sort: DashboardGrossCustomDataSort(props.groupBy),
                fields: CrmGrossFields,
            })
            if (props.groupBy === 'date') {
                setData(res.data.data);
            } else {
                const _data: DashboardGrossCustomGrossPerfItem[] = res.data.data;
                _data.sort((a, b) => {
                    if (a.report_item_payment.gross_total_sum > b.report_item_payment.gross_total_sum) {
                        return -1;
                    }
                    if (a.report_item_payment.gross_total_sum < b.report_item_payment.gross_total_sum) {
                        return 1;
                    }
                    return 0;
                })
                setData(_data);
            }
        }
    }

    useEffect(() => {
        props.loading.set(
            grosPerfData.loading || adSpendFbData.loading || adSpendTkData.loading || recurringData.loading ||
            adSpendFbAllData.loading || adSpendTkAllData.loading || adSpendGoogleAllData.loading
        )
    }, [
        grosPerfData.loading, adSpendFbData.loading, adSpendTkData.loading, recurringData.loading,
        adSpendFbAllData, adSpendTkAllData, adSpendGoogleAllData
    ]);

    useEffect(() => {
        loadGrossPerformanceData().catch(console.error)
    }, [props.config, props.groupBy, props.reloadKey]);

    const tbl = Table.default;

    const showAdSpend = props.groupBy !== 'slug';

    return (
        <Card.WhiteCard
            style={{
                body: {
                    paddingHorizontal: 'p-2'
                }
            }}
        >
            <tbl.Table
                thead={
                    <thead>
                        <tr className='sticky top-0 bg-white'>
                            <tbl.Th props={{ className: classNames('sticky left-0', borderBR, ' bg-white') }}>{""}</tbl.Th>

                            <tbl.Th props={{ className: classNames('text-right', borderBottom) }}>Initial</tbl.Th>

                            <tbl.Th props={{ className: classNames('text-right', borderBL) }}>Initial (subs)</tbl.Th>
                            <tbl.Th props={{ className: classNames('text-right', borderBottom) }}>Initial (lifetime)</tbl.Th>
                            <tbl.Th props={{ className: classNames('text-right', borderBottom) }}>Initial (upsells)</tbl.Th>

                            <tbl.Th props={{ className: classNames('text-right', borderBL) }}>Subscriptions</tbl.Th>

                            <tbl.Th props={{ className: classNames('text-right', borderBL) }}>Ad spend</tbl.Th>
                            <tbl.Th props={{ className: classNames('text-right', borderBottom) }}>FB</tbl.Th>
                            <tbl.Th props={{ className: classNames('text-right', borderBottom) }}>TK</tbl.Th>
                            <tbl.Th props={{ className: classNames('text-right', borderBottom) }}>GG</tbl.Th>

                            <tbl.Th props={{ className: classNames('text-right', borderBL) }}>CPA</tbl.Th>

                            <tbl.Th props={{ className: classNames('text-right', borderBL) }}>AOV</tbl.Th>
                            <tbl.Th props={{ className: classNames('text-right', borderBottom) }}>ROAS</tbl.Th>

                            {isAdmin && <Fragment>
                                <tbl.Th props={{ className: classNames('text-right', borderBL) }}>Recurring</tbl.Th>

                                <tbl.Th props={{ className: classNames('text-right', borderBL) }}>Gross performance</tbl.Th>
                            </Fragment>}
                        </tr>
                    </thead>
                }
                tbody={
                    <tbody>
                        {data.map(el => {
                            const id = getValueByPath(el, props.groupBy);

                            let fbAdSpend = !showAdSpend ? 0 : (adSpendFbData.data.filter(a => getValueByPath(a, props.groupBy) === id).map(a => a.total_sum).reduce((a, b) => a + b, 0));
                            let tkAdSpend = !showAdSpend ? 0 : (adSpendTkData.data.filter(a => getValueByPath(a, props.groupBy) === id).map(a => a.total_sum).reduce((a, b) => a + b, 0));
                            let googleAdSpend = 0;
                            if (props.config.filters.ad_cost_substraction) {
                                const fbAllAdSpend = !showAdSpend ? 0 : (adSpendFbAllData.data.filter(a => getValueByPath(a, props.groupBy) === id).map(a => a.total_sum).reduce((a, b) => a + b, 0));
                                const tkAllAdSpend = !showAdSpend ? 0 : (adSpendTkAllData.data.filter(a => getValueByPath(a, props.groupBy) === id).map(a => a.total_sum).reduce((a, b) => a + b, 0));
                                const googleAllAdSpend = !showAdSpend ? 0 : (adSpendGoogleAllData.data.filter(a => getValueByPath(a, props.groupBy) === id).map(a => a.total_sum).reduce((a, b) => a + b, 0));

                                fbAdSpend = fbAllAdSpend - fbAdSpend;
                                tkAdSpend = tkAllAdSpend - tkAdSpend;
                                googleAdSpend = googleAllAdSpend - googleAdSpend;
                            }
                            const adSpend = fbAdSpend + tkAdSpend + googleAdSpend;

                            const recurring = recurringData.data.filter(a => getValueByPath(a, DashboardGrossCustomRecurringGroupByPath(props.groupBy)) === id).map(a => a.total_sum).reduce((a, b) => a + b, 0);

                            const cpa = el.id_count > 0 ? adSpend / el.id_count : adSpend;

                            const aov = el.id_count > 0 ? el.report_item_payment.gross_total_sum / el.id_count : 0;

                            const roas = adSpend ? el.report_item_payment.gross_total_sum / adSpend : 0;

                            return (
                                <tr key={`row-${id}`}>
                                    <tbl.Td props={{ className: classNames('sticky left-0', borderRight, ' bg-white') }}>{id}</tbl.Td>

                                    <tbl.Td props={{ className: classNames('text-right') }}>{floatFormat(el.report_item_payment.gross_total_sum)}</tbl.Td>

                                    <tbl.Td props={{ className: classNames('text-right', borderLeft) }}>{floatFormat(el.report_item_payment.gross_initial_sub_total_sum - el.report_item_payment.gross_initial_lifetime_total_sum)}</tbl.Td>
                                    <tbl.Td props={{ className: classNames('text-right') }}>{floatFormat(el.report_item_payment.gross_initial_lifetime_total_sum)}</tbl.Td>
                                    <tbl.Td props={{ className: classNames('text-right') }}>{floatFormat(el.report_item_payment.gross_upsell_total_sum)}</tbl.Td>

                                    <tbl.Td props={{ className: classNames('text-right', borderLeft) }}>{intFormat(el.id_count)}</tbl.Td>

                                    <tbl.Td props={{ className: classNames('text-right', borderLeft) }}>{floatFormat(adSpend)}</tbl.Td>
                                    <tbl.Td props={{ className: classNames('text-right') }}>{floatFormat(fbAdSpend)}</tbl.Td>
                                    <tbl.Td props={{ className: classNames('text-right') }}>{floatFormat(tkAdSpend)}</tbl.Td>
                                    <tbl.Td props={{ className: classNames('text-right') }}>{floatFormat(googleAdSpend)}</tbl.Td>

                                    <tbl.Td props={{ className: classNames('text-right', borderLeft) }}>{floatFormat(cpa)}</tbl.Td>

                                    <tbl.Td props={{ className: classNames('text-right', borderLeft) }}>{floatFormat(aov)}</tbl.Td>
                                    <tbl.Td props={{ className: classNames('text-right') }}>{floatFormat(roas)}</tbl.Td>

                                    {isAdmin && <Fragment>
                                        <tbl.Td props={{ className: classNames('text-right', borderLeft) }}>{floatFormat(recurring)}</tbl.Td>

                                        <tbl.Td props={{ className: classNames('text-right', borderLeft) }}>{floatFormat(el.report_item_payment.gross_total_sum + recurring - adSpend)}</tbl.Td>
                                    </Fragment>}

                                </tr>
                            )
                        })}
                        <OtherLine
                            data={data}
                            fbData={adSpendFbData.data}
                            tkData={adSpendTkData.data}

                            fbDataAll={adSpendFbAllData.data}
                            tkDataAll={adSpendTkAllData.data}
                            googleDataAll={adSpendGoogleAllData.data}
                            adCostSubstraction={props.config.filters.ad_cost_substraction}

                            recurringData={recurringData.data}
                            groupBy={props.groupBy}
                        />
                        <TotalLine
                            data={data}

                            fbData={adSpendFbData.data}
                            tkData={adSpendTkData.data}

                            fbDataAll={adSpendFbAllData.data}
                            tkDataAll={adSpendTkAllData.data}
                            googleDataAll={adSpendGoogleAllData.data}
                            adCostSubstraction={props.config.filters.ad_cost_substraction}

                            recurringData={recurringData.data}
                        />
                    </tbody>
                }
            />
        </Card.WhiteCard>
    )
}

type OtherLineProps = {
    data: DashboardGrossCustomGrossPerfItem[],

    fbData: DashboardGrossCustomAdSpendItem[],
    tkData: DashboardGrossCustomAdSpendItem[],

    fbDataAll: DashboardGrossCustomAdSpendItem[],
    tkDataAll: DashboardGrossCustomAdSpendItem[],
    googleDataAll: DashboardGrossCustomAdSpendItem[],
    adCostSubstraction?: boolean,

    recurringData: DashboardGrossCustomAdSpendItem[],
    groupBy: DashboardGrossCustomGroupBy,

}

const OtherLine = ({ data, fbData, tkData, recurringData, groupBy, adCostSubstraction, fbDataAll, tkDataAll, googleDataAll, }: OtherLineProps) => {
    const tbl = Table.default;

    const ids = data.map(el => getValueByPath(el, groupBy))


    const allGrossTotalSum = 0

    let fbAdSpend = fbData.filter(a => ids.indexOf(getValueByPath(a, (groupBy))) === -1).map(a => a.total_sum).reduce((a, b) => a + b, 0);
    let tkAdSpend = tkData.filter(a => ids.indexOf(getValueByPath(a, (groupBy))) === -1).map(a => a.total_sum).reduce((a, b) => a + b, 0);
    let googleAdSpend = 0;
    if (adCostSubstraction) {
        const fbAdSpendAll = fbDataAll.filter(a => ids.indexOf(getValueByPath(a, (groupBy))) === -1).map(a => a.total_sum).reduce((a, b) => a + b, 0);
        const tkAdSpendAll = tkDataAll.filter(a => ids.indexOf(getValueByPath(a, (groupBy))) === -1).map(a => a.total_sum).reduce((a, b) => a + b, 0);
        const googleAdSpendAll = googleDataAll.filter(a => ids.indexOf(getValueByPath(a, (groupBy))) === -1).map(a => a.total_sum).reduce((a, b) => a + b, 0);

        fbAdSpend = fbAdSpendAll - fbAdSpend;
        tkAdSpend = tkAdSpendAll - tkAdSpend;
        googleAdSpend = googleAdSpendAll - googleAdSpend;
    }

    const adSpend = fbAdSpend + tkAdSpend + googleAdSpend;

    const recurring = recurringData.filter(a => ids.indexOf(getValueByPath(a, DashboardGrossCustomRecurringGroupByPath(groupBy))) === -1).map(a => a.total_sum).reduce((a, b) => a + b, 0);

    const { userState } = useTemplatesCore();
    const isAdmin = checkPermission(userState, Permissions.Enum.admin);

    return (
        <tr className='italic'>
            <tbl.Td props={{ className: classNames('sticky left-0', borderRight) }}>{"Other"}</tbl.Td>

            <tbl.Td props={{ className: classNames('text-right',) }}>{floatFormat(0)}</tbl.Td>

            <tbl.Td props={{ className: classNames('text-right', borderLeft) }}>{floatFormat(0)}</tbl.Td>
            <tbl.Td props={{ className: classNames('text-right',) }}>{floatFormat(0)}</tbl.Td>
            <tbl.Td props={{ className: classNames('text-right',) }}>{floatFormat(0)}</tbl.Td>

            <tbl.Td props={{ className: classNames('text-right', borderLeft) }}>{intFormat(0)}</tbl.Td>

            <tbl.Td props={{ className: classNames('text-right', borderLeft) }}>{floatFormat(adSpend)}</tbl.Td>
            <tbl.Td props={{ className: classNames('text-right',) }}>{floatFormat(fbAdSpend)}</tbl.Td>
            <tbl.Td props={{ className: classNames('text-right',) }}>{floatFormat(tkAdSpend)}</tbl.Td>
            <tbl.Td props={{ className: classNames('text-right',) }}>{floatFormat(googleAdSpend)}</tbl.Td>

            <tbl.Td props={{ className: classNames('text-right', borderLeft) }}>{floatFormat(0)}</tbl.Td>

            <tbl.Td props={{ className: classNames('text-right', borderLeft) }}>{floatFormat(0)}</tbl.Td>
            <tbl.Td props={{ className: classNames('text-right',) }}>{floatFormat(0)}</tbl.Td>


            {isAdmin && <Fragment>
                <tbl.Td props={{ className: classNames('text-right', borderLeft) }}>{floatFormat(recurring)}</tbl.Td>

                <tbl.Td props={{ className: classNames('text-right', borderLeft) }}>{floatFormat(allGrossTotalSum + recurring - adSpend)}</tbl.Td>
            </Fragment>}
        </tr>
    )
}

type TotalLineProps = {
    data: DashboardGrossCustomGrossPerfItem[],

    fbData: DashboardGrossCustomAdSpendItem[],
    tkData: DashboardGrossCustomAdSpendItem[],

    fbDataAll: DashboardGrossCustomAdSpendItem[],
    tkDataAll: DashboardGrossCustomAdSpendItem[],
    googleDataAll: DashboardGrossCustomAdSpendItem[],
    adCostSubstraction?: boolean,

    recurringData: DashboardGrossCustomAdSpendItem[],
}

const TotalLine = ({ data, fbData, tkData, recurringData, adCostSubstraction, fbDataAll, tkDataAll, googleDataAll, }: TotalLineProps) => {
    const { userState } = useTemplatesCore();
    const isAdmin = checkPermission(userState, Permissions.Enum.admin);

    const tbl = Table.default;

    const allGrossTotalSum = data.map(el => el.report_item_payment.gross_total_sum).reduce((a, b) => a + b, 0)
    const allGrossInitialSubTotalSum = data.map(el => el.report_item_payment.gross_initial_sub_total_sum).reduce((a, b) => a + b, 0)
    const allGrossInitialLifetimeTotalSum = data.map(el => el.report_item_payment.gross_initial_lifetime_total_sum).reduce((a, b) => a + b, 0)
    const allGrossInitialUpsellTotalSum = data.map(el => el.report_item_payment.gross_upsell_total_sum).reduce((a, b) => a + b, 0)

    const allSubs = data.map(el => el.id_count).reduce((a, b) => a + b, 0)

    let fbAdSpend = fbData.map(a => a.total_sum).reduce((a, b) => a + b, 0);
    let tkAdSpend = tkData.map(a => a.total_sum).reduce((a, b) => a + b, 0);
    let googleAdSpend = 0;
    if (adCostSubstraction) {
        const fbAdSpendAll = fbDataAll.map(a => a.total_sum).reduce((a, b) => a + b, 0);
        const tkAdSpendAll = tkDataAll.map(a => a.total_sum).reduce((a, b) => a + b, 0);
        const googleAdSpendAll = googleDataAll.map(a => a.total_sum).reduce((a, b) => a + b, 0);

        fbAdSpend = fbAdSpendAll - fbAdSpend;
        tkAdSpend = tkAdSpendAll - tkAdSpend;
        googleAdSpend = googleAdSpendAll - googleAdSpend;

    }

    const adSpend = fbAdSpend + tkAdSpend;

    const cpa = allSubs > 0 ? adSpend / allSubs : adSpend;

    const aov = allSubs > 0 ? allGrossTotalSum / allSubs : 0;

    const roas = adSpend ? allGrossTotalSum / adSpend : 0;

    const recurring = recurringData.map(a => a.total_sum).reduce((a, b) => a + b, 0);

    return (
        <tr className='font-bold'>
            <tbl.Td props={{ className: classNames('sticky left-0', borderTR) }}>{"Total"}</tbl.Td>

            <tbl.Td props={{ className: classNames('text-right', borderTop) }}>{floatFormat(allGrossTotalSum)}</tbl.Td>

            <tbl.Td props={{ className: classNames('text-right', borderTL) }}>{floatFormat(allGrossInitialSubTotalSum - allGrossInitialLifetimeTotalSum)}</tbl.Td>
            <tbl.Td props={{ className: classNames('text-right', borderTop) }}>{floatFormat(allGrossInitialLifetimeTotalSum)}</tbl.Td>
            <tbl.Td props={{ className: classNames('text-right', borderTop) }}>{floatFormat(allGrossInitialUpsellTotalSum)}</tbl.Td>

            <tbl.Td props={{ className: classNames('text-right', borderTL) }}>{intFormat(allSubs)}</tbl.Td>

            <tbl.Td props={{ className: classNames('text-right', borderTL) }}>{floatFormat(adSpend)}</tbl.Td>
            <tbl.Td props={{ className: classNames('text-right', borderTop) }}>{floatFormat(fbAdSpend)}</tbl.Td>
            <tbl.Td props={{ className: classNames('text-right', borderTop) }}>{floatFormat(tkAdSpend)}</tbl.Td>
            <tbl.Td props={{ className: classNames('text-right', borderTop) }}>{floatFormat(googleAdSpend)}</tbl.Td>

            <tbl.Td props={{ className: classNames('text-right', borderTL) }}>{floatFormat(cpa)}</tbl.Td>

            <tbl.Td props={{ className: classNames('text-right', borderTL) }}>{floatFormat(aov)}</tbl.Td>
            <tbl.Td props={{ className: classNames('text-right', borderTop) }}>{floatFormat(roas)}</tbl.Td>


            {isAdmin && <Fragment>
                <tbl.Td props={{ className: classNames('text-right', borderTL) }}>{floatFormat(recurring)}</tbl.Td>

                <tbl.Td props={{ className: classNames('text-right', borderTL) }}>{floatFormat(allGrossTotalSum + recurring - adSpend)}</tbl.Td>
            </Fragment>}
        </tr>
    )
}

export const getValueByPath = (el: any, path: string) => {
    let value: any = "";
    try {
        value = path.split('.').reduce((previous: any, current: any) => previous[current], el);
    } catch (e) {
    }
    return value;
}

const floatFormat = (value: number) => numberFormat({
    userInput: value,
    decimals: 2,
    decPoint: '.',
    thousandsSep: ' '
})

const intFormat = (value: number) => numberFormat({
    userInput: value,
    decimals: 0,
    decPoint: '.',
    thousandsSep: ' '
})

const borderRight = 'shadow-[inset_-1px_0px_0px_0px_rgba(96,165,250,1)]';
const borderLeft = 'shadow-[inset_1px_0px_0px_0px_rgba(96,165,250,1)]';
const borderTL = 'shadow-[inset_1px_1px_0px_0px_rgba(96,165,250,1)]';
const borderTR = 'shadow-[inset_-1px_1px_0px_0px_rgba(96,165,250,1)]';
const borderTop = 'shadow-[inset_0px_1px_0px_0px_rgba(96,165,250,1)]';
const borderBottom = 'shadow-[inset_0px_-1px_0px_0px_rgba(96,165,250,1)]';
const borderBL = 'shadow-[inset_1px_-1px_0px_0px_rgba(96,165,250,1)]';
const borderBR = 'shadow-[inset_-1px_-1px_0px_0px_rgba(96,165,250,1)]';